import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  zeitaufzeichnungAPI,
  Zeitaufzeichnung as ZeitaufzeichnungType,
  ZeitaufzeichnungCreate
} from '../services/zeitaufzeichnungApi';
import ZeitaufzeichnungForm from './ZeitaufzeichnungForm';
import '../styles/Zeitaufzeichnung.css';

const WEEKDAYS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const MONTH_NAMES = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

const Zeitaufzeichnung: React.FC = () => {
  const { accessToken } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [zeitaufzeichnungen, setZeitaufzeichnungen] = useState<ZeitaufzeichnungType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | undefined>();
  const [editingRecord, setEditingRecord] = useState<ZeitaufzeichnungType | undefined>();

  const currentMonth = currentDate.toISOString().slice(0, 7); // YYYY-MM

  useEffect(() => {
    loadZeitaufzeichnungen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth, accessToken]);

  const loadZeitaufzeichnungen = async () => {
    if (!accessToken) return;

    setIsLoading(true);
    setError(null);
    try {
      const data = await zeitaufzeichnungAPI.fetchZeitaufzeichnungen(currentMonth, accessToken);
      setZeitaufzeichnungen(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Laden der Zeitaufzeichnungen');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleAddNew = (dateStr?: string) => {
    setSelectedDate(dateStr);
    setEditingRecord(undefined);
    setShowForm(true);
  };

  const handleEdit = (record: ZeitaufzeichnungType) => {
    setEditingRecord(record);
    setSelectedDate(undefined);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!accessToken) return;
    if (!window.confirm('Möchten Sie diese Zeitaufzeichnung wirklich löschen?')) return;

    try {
      await zeitaufzeichnungAPI.deleteZeitaufzeichnung(id, accessToken);
      await loadZeitaufzeichnungen();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleFormSubmit = async (data: ZeitaufzeichnungCreate) => {
    if (!accessToken) return;

    try {
      if (editingRecord) {
        await zeitaufzeichnungAPI.updateZeitaufzeichnung(editingRecord.id, data, accessToken);
      } else {
        await zeitaufzeichnungAPI.createZeitaufzeichnung(data, accessToken);
      }
      setShowForm(false);
      setEditingRecord(undefined);
      setSelectedDate(undefined);
      await loadZeitaufzeichnungen();
    } catch (err: any) {
      throw err;
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingRecord(undefined);
    setSelectedDate(undefined);
  };

  // Generate all days of the month
  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      // Format date in local timezone to avoid UTC conversion issues
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const weekday = date.getDay();
      const isWeekend = weekday === 0 || weekday === 6;

      const record = zeitaufzeichnungen.find(z => z.datum === dateStr);

      days.push({
        date: dateStr,
        day,
        weekday: WEEKDAYS[weekday],
        isWeekend,
        record
      });
    }

    return days;
  };

  const calculateMonthlyTotal = () => {
    return zeitaufzeichnungen.reduce((total, record) => {
      const hours = parseFloat(record.arbeitszeit_stunden) || 0;
      return total + hours;
    }, 0);
  };

  const days = getDaysInMonth();
  const monthlyTotal = calculateMonthlyTotal();

  return (
    <div className="zeitaufzeichnung-container">
      <div className="zeitaufzeichnung-header">
        <h1>Zeitaufzeichnung</h1>
        <button onClick={() => handleAddNew()} className="btn-primary">
          Neue Eintragung
        </button>
      </div>

      <div className="month-navigation">
        <button onClick={handlePreviousMonth} className="btn-nav">
          &lt; Vorheriger Monat
        </button>
        <h2>
          {MONTH_NAMES[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h2>
        <button onClick={handleNextMonth} className="btn-nav">
          Nächster Monat &gt;
        </button>
      </div>

      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="loading">Laden...</div>
      ) : (
        <>
          <div className="zeitaufzeichnung-table-wrapper">
            <table className="zeitaufzeichnung-table">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Wochentag</th>
                  <th>Beginn</th>
                  <th>Ende</th>
                  <th>Mittagspause</th>
                  <th>Verschmutzung</th>
                  <th>Arbeitszeit</th>
                  <th>Notiz</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {days.map(({ date, day, weekday, isWeekend, record }) => {
                  const getVerschmutzungDisplay = (grad?: string | null) => {
                    if (!grad) return '-';
                    switch (grad) {
                      case 'STARK': return '🔴 Stark';
                      case 'MITTEL': return '🟡 Mittel';
                      case 'LEICHT': return '🟢 Leicht';
                      default: return '-';
                    }
                  };

                  return (
                    <tr key={date} className={isWeekend ? 'weekend-row' : ''}>
                      <td>{day}.{currentDate.getMonth() + 1}.{currentDate.getFullYear()}</td>
                      <td className={isWeekend ? 'weekend-text' : ''}>{weekday}</td>
                      <td>{record?.beginn || '-'}</td>
                      <td>{record?.ende || '-'}</td>
                      <td>{record ? `${record.mittagspause_minuten} min` : '-'}</td>
                      <td className="verschmutzung-cell">
                        {getVerschmutzungDisplay(record?.verschmutzungsgrad)}
                      </td>
                      <td className="arbeitszeit-cell">
                        {record ? `${record.arbeitszeit_stunden} h` : '-'}
                      </td>
                      <td className="notiz-cell">{record?.notiz || '-'}</td>
                      <td className="actions-cell">
                        {record ? (
                          <>
                            <button
                              onClick={() => handleEdit(record)}
                              className="btn-action btn-edit"
                              title="Bearbeiten"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => handleDelete(record.id)}
                              className="btn-action btn-delete"
                              title="Löschen"
                            >
                              🗑️
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => handleAddNew(date)}
                            className="btn-action btn-add"
                            title="Hinzufügen"
                          >
                            +
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td colSpan={6}><strong>Monatssumme:</strong></td>
                  <td colSpan={3}>
                    <strong>{monthlyTotal.toFixed(2)} h</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}

      {showForm && (
        <ZeitaufzeichnungForm
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          initialData={editingRecord}
          selectedDate={selectedDate}
        />
      )}
    </div>
  );
};

export default Zeitaufzeichnung;
